import { React, _n } from 'pexui';
import { YodleeBasePage } from './yodleebase';
import { showError } from '../dialog';
import * as utils from '../../utilities/utils';
import { track, trackClick } from '../../utilities/track';
import { printElements } from '../../utilities/print';
import { stopBusy } from '../comps/busy';
import { DocUploadService } from '../../services/docuploadsvc';
import { flow, getTrackLabel } from '../../models/flow';
import { Html } from '../comps/html';

const { Promise } = utils;

const prependLeadingNumber = (astr, num) => {
  // already lead by a number, which could be inside a decorating tag such as <b>
  if (/^\s*(\<[^\>]+\>)?\s*\d+/.test(astr)) {
    return astr;
  }
  return `<span class="infw-how-leading-num infw-show-sm">${num}. </span>${astr}`;
};

const HowToStep = ({ stepNumber, icon }) => (
  <div
    id={`infw_yodlee_how_section_${stepNumber}`}
    className="infw-yodlee-how-section"
    role="listitem"
  >
    <div className="infw-list-item">
      <div className="infw-yodlee-how-icons-area">
        <div className="infw-yodlee-how-badge">
          <span
            id={`infw_yodlee_how_badge_${stepNumber}`}
            className="infw-circle-badge-icon"
            aria-hidden="true"
          >
            {`${stepNumber}`}
          </span>
        </div>
        <div
          className="infw-yodlee-how-icon"
          aria-hidden="true"
        >
          <span
            id={`infw_yodlee_how_icon_${stepNumber}`}
            className={`icon-md dls-accent-blue-02 ${icon}`}
          />
        </div>
      </div>
      <div className="infw-yodlee-how-text-area">
        <Html
          tag="p"
          id={`infw_yodlee_home_item_${stepNumber}`}
          className="infw-list-text-rws"
          content={prependLeadingNumber(_n(`yodlee.home.item.${stepNumber}`), stepNumber)}
        />
        <Html
          tag="p"
          id={`infw_yodlee_home_desc_${stepNumber}`}
          className="infw-yodlee-step-desc"
          content={`yodlee.home.desc.${stepNumber}`}
          hideEmpty={true}
        />
      </div>
    </div>
  </div>
);

const defaultIcons = [
  'dls-icon-bank',
  'dls-icon-lock',
  'dls-icon-time',
];

const howToSteps = (props) => {
  const contents = [];
  let stepNumber = 1;
  while (_n(`yodlee.home.item.${stepNumber}`)) {
    contents.push(<HowToStep
      stepNumber={stepNumber}
      icon={_n(`yodlee.home.icon.${stepNumber}`) || (stepNumber <= 3 ? defaultIcons[stepNumber - 1] : '')}
    />);
    ++stepNumber;
  }
  return contents;
};

export class YodleePage extends YodleeBasePage {
  constructor(props) {
    super(props);
    this.state = {
      tncConfirmed: false,
    };
  }

  componentDidMount() {
    flow.lastChannel = 'YODLEE';
    track('Yodlee:TnC', { contentName: getTrackLabel(flow.currNode) });
  }

  onConfirm(evt) {
    this.setState({ tncConfirmed: evt.target.checked });
  }

  getTnCText() {
    const text = utils.getElement('infw_yodlee_tnc').innerText;
    // text = text.replace(/[^\w\.,]/g, ' ');
    return text;
  }

  handleTncCheckbox({ keyCode }) {
    const spacebarKeyCode = 32;

    if (keyCode === spacebarKeyCode) {
      setTimeout(() => document.getElementById('infw_check_yodlee_tnc').focus(), 500);
    }
  }

  onPrint(evt) {
    evt.preventDefault();

    trackClick('Click_Print');

    printElements([
      'infw_yodlee_howto',
      'infw_yodlee_tnc'],
    `.infw-circle-badge-icon{display:none;}
         #infw_yodlee_howto p.body-1.margin-0-b.printable{font-weight:bold;font-size:150%;}
        `
    );
  }

  submitYodleeRequest() {
    trackClick('TnCContinue');
    if (this.state.tncConfirmed) {
      flow.lastNodeForYodlee = flow.currNode;
      flow.lastNodeForYodlee.messages = [];
      flow.yodleeAttributes = ''; // clean any previous value
      flow.tnc = '';

      const tnc = this.getTnCText();

      // in parallel
      const callSaveTnc = DocUploadService.saveTnCAcceptance(
        flow.req.transaction_id,
        flow.req.originator_id,
        flow.req.document_data.process_control_number,
        'YODLEE',
        tnc,
        true
      );

      // TODO - uncomment when yodleeOptOut screen is implemented
      // const isCheckingOptInStatus = flow.isRecurr && flow.is_sso_flow;
      const isCheckingOptInStatus = false;
      const conditionallyCallOptInStatus = isCheckingOptInStatus
        ? DocUploadService
          .getOptInStatus(flow.req)
          .then((response) => {
            flow.isOptIn = response.opt_status && response.opt_status === 'OPTED-IN';
          })
        : Promise.resolve();

      // when all finish
      utils.Promise.all([
        callSaveTnc,
        conditionallyCallOptInStatus,
      ]).then(() => {
        stopBusy();
        if (flow.isOptIn) {
          // TODO - add yodleeOptOut screen
          this.props.widget.nav('yodleeOptOut');
        } else {
          this.props.widget.nav('yodleeSubmit');
        }
      }).catch((err) => {
        console.error(err);
        const errorKey = 'Yodlee.failed.message';
        flow.lastError = 'failed';
        flow.addMessage(`!${_n(errorKey)}`, errorKey);
        stopBusy();
        this.props.widget.nav('index');
      });
    } else {
      stopBusy();
      showError('Please confirm the terms and conditions!');
    }
  }

  hasFooter() {
    return true;
  }

  hasHelp() {
    return true;
  }

  getFooterButtons() {
    const yodleeOnly = flow.isYodleeOnly();
    return [{
      id: 'infw_btn_cancel_yodlee',
      label: _n('yodlee.tnc.button_cancel'),
      isPrimary: false,
      onClick: this.onCancel.bind(this),
    },
    {
      id: 'infw_btn_continue',
      label: _n('yodlee.tnc.button_continue'),
      isPrimary: true,
      disabled: !this.state.tncConfirmed,
      busyZone: 'widget',
      onClick: this.submitYodleeRequest.bind(this),
    },
    ];
  }

  renderContent() {
    const tncPdfUrl = utils.getUrlFromResource('yodlee.tnc.pdf');

    return (
      <div className="card">

        <div className="infw-message-wrapper">
          <Html className="infw-message" content="yodlee.home.title" />
        </div>
        <hr className="card-separator" />

        <div id="infw_yodlee_howto" className="infw-yodlee-how-container1">

          <div className="infw-yodlee-how-container2">
            <div className="infw-yodlee-how-container3">
              <Html
                tag="p"
                className="infw-yodlee-how-subtitle"
                content="yodlee.home.how"
              />
            </div>
            <span role="list">
              {howToSteps()}
            </span>
          </div>
          <hr className="card-separator" />

        </div>

        <Html id="infw_yodlee_tnc" className="infw-yodlee-tnc-box" content="yodlee.tnc.stmt" />

        <div id="infw_yodlee_tnc_op_links" className="infw-yodlee-tnc-links">
          <a
            id="infw_link_yodlee_print"
            onClick={this.onPrint.bind(this)}
            href={tncPdfUrl}
          >
            {_n('yodlee.tnc.button_print')}
          </a>
          <span> | </span>
          <a
            id="infw_link_yodlee_save"
            onClick={(evt) => { trackClick('Click_Save'); }}
            href={tncPdfUrl}
            target="_blank"
          >
            {_n('yodlee.tnc.button_save')}
          </a>
        </div>

        <hr className="card-separator" />
        <div className="infw-yodlee-tnc-confirm-container1">
          <div className="infw-yodlee-tnc-confirm-container2">
            <div className="infw-yodlee-tnc-confirm-container-chk">
              <input
                type="checkbox"
                aria-label="tnc confirmation checkbox"
                id="infw_check_yodlee_tnc"
                className="infw-checkbox"
                checked={this.state.tncConfirmed}
                onClick={this.onConfirm.bind(this)}
                onKeyDown={this.handleTncCheckbox.bind(this)}
              />
            </div>
            <label for="infw_check_yodlee_tnc">{_n('yodlee.tnc.confirm.label')}</label>
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./application/src/views/yodlee/yodlee.jsx